import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, CheckCircle2, Clock, Download, FileText, Plus, ReceiptText, SearchX, X } from 'lucide-react';
import {
  ActionList,
  ActionListItem,
  Alert,
  Button,
  CoverageMeter,
  EmptyState,
  Icon,
  KeyValueList,
  KeyValueRow,
  PageHeader,
  ProductThumb,
  ProgressBar,
  StatusPill,
  WhatsAppContactCard,
} from '@/components/common';
import type { PillTone } from '@/components/common/StatusPill';
import { useCustomerClaims } from '@/hooks/useCustomerClaims';
import { useCustomerProducts } from '@/hooks/useCustomerProducts';
import { useFileClaim } from '@/hooks/useFileClaim';
import type { Policy } from '@/types';
import type { ProductCase } from '@/types/product';
import { GST_PATH } from '@/utils/gst';
import { comingSoon } from '@/utils/orderPresentation';
import { planTermsPath } from '@/utils/merchantTerms';
import { coveredItems, notCoveredItems, siteName } from '@/utils/planDetails';
import { coverRemaining, formatDay, formatRupees, formatTimeLeft, planKind } from '@/utils/productCases';

const BACK = { to: '/customer/policies', label: 'My Plans' };
const card = 'tw-flex tw-flex-col tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5';

const STATUS: Record<Policy['status'], { tone: PillTone; label: string }> = {
  active: { tone: 'success', label: 'Active' },
  pending: { tone: 'warning', label: 'Pending' },
  expired: { tone: 'neutral', label: 'Expired' },
};

const HOW_CLAIMS_WORK = [
  { icon: FileText, title: 'File a claim here or on WhatsApp', text: 'Add photos and a short description of what happened.' },
  { icon: Clock, title: 'We review it within 2 working days', text: 'We may ask for one more photo or your invoice.' },
  { icon: CheckCircle2, title: 'We resolve it', text: 'By repair, replacement or store credit — never cash.' },
];

const policyDate = (policy: Policy, event: string) => policy.timeline?.find((t) => t.event === event)?.date;

const openCertificate = (policy: Policy) =>
  policy.certificateUrl ? window.open(policy.certificateUrl, '_blank', 'noopener') : comingSoon('Downloading the plan certificate');

/** Covered / not covered lists with the plan's terms link (Figma "What's covered"). */
const Coverage: React.FC<{ policy: Policy }> = ({ policy }) => (
  <section aria-labelledby="whats-covered" className={`${card} tw-gap-4`}>
    <div className="tw-flex tw-flex-col tw-gap-3">
      <h2 id="whats-covered" className="tw-text-[17px] tw-font-semibold tw-leading-6 tw-text-ds-text-primary">
        What’s covered
      </h2>
      <ul className="tw-flex tw-flex-col tw-gap-2.5">
        {coveredItems(policy).map((item) => (
          <li key={item} className="tw-flex tw-items-start tw-gap-2.5 tw-text-[14px] tw-leading-5 tw-text-ds-text-primary">
            <Icon icon={Check} size={16} className="tw-mt-0.5 tw-text-ds-success-fg" />
            {item}
          </li>
        ))}
      </ul>
    </div>
    <div className="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-ds-border-default tw-pt-4">
      <h2 className="tw-text-[17px] tw-font-semibold tw-leading-6 tw-text-ds-text-primary">Not covered</h2>
      <ul className="tw-flex tw-flex-col tw-gap-2.5">
        {notCoveredItems(policy).map((item) => (
          <li key={item} className="tw-flex tw-items-start tw-gap-2.5 tw-text-[14px] tw-leading-5 tw-text-ds-text-secondary">
            <Icon icon={X} size={16} className="tw-mt-0.5 tw-text-ds-danger-fg" />
            {item}
          </li>
        ))}
      </ul>
    </div>
    <Button variant="link" className="tw-self-start" href={planTermsPath(policy)} target="_blank">
      Plan terms and conditions
    </Button>
  </section>
);

const HowClaimsWork: React.FC = () => (
  <section aria-labelledby="how-claims-work" className={`${card} tw-gap-4`}>
    <h2 id="how-claims-work" className="tw-text-[17px] tw-font-semibold tw-leading-6 tw-text-ds-text-primary">
      How claims work
    </h2>
    <ol className="tw-flex tw-flex-col tw-gap-4">
      {HOW_CLAIMS_WORK.map((step) => (
        <li key={step.title} className="tw-flex tw-items-start tw-gap-3">
          <span className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-ds-brand-tint tw-text-ds-brand-text">
            <Icon icon={step.icon} />
          </span>
          <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
            <span className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{step.title}</span>
            <span className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{step.text}</span>
          </span>
        </li>
      ))}
    </ol>
  </section>
);

/** Plan details rows from the policy and its order; rows the API has no data for are left out. */
function detailRows(product: ProductCase, plan: NonNullable<ProductCase['plan']>): KeyValueRow[] {
  const { policy } = plan;
  const extended = planKind(policy.type) === 'extended';
  const price = product.price ?? policy.productPrice;
  const boughtOn = policyDate(policy, 'Policy Created');
  const site = siteName(policy.merchantUrl);
  return [
    { label: 'Plan', value: plan.name },
    { label: 'Policy ID', value: policy.name },
    plan.startDate && { label: 'Cover period', value: `${formatDay(plan.startDate)} – ${formatDay(plan.endDate)}` },
    price && { label: 'Covered up to', value: `${formatRupees(price)}${extended ? ' in total' : ''} · invoice value incl. GST` },
    policy.premium > 0 && { label: 'Plan price', value: [formatRupees(policy.premium), boughtOn && `bought ${formatDay(boughtOn)}`].filter(Boolean).join(' · ') },
    product.merchant && { label: 'Sold with', value: [product.merchant, site].filter(Boolean).join(' · ') },
  ].filter(Boolean) as KeyValueRow[];
}

/** T1A / T1B Plan details: the plan on one product — cover, what's covered, claims and documents. */
const PlanDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { products, loading, error, reload } = useCustomerProducts();
  const { claims } = useCustomerClaims();
  const { fileClaim, busyPolicyId } = useFileClaim();
  const product = products.find((p) => p.plan && String(p.plan.policy.id) === id);

  if (loading || error || !product?.plan) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-6">
        <PageHeader title="Plan details" back={BACK} />
        {loading ? (
          <div aria-busy className="tw-h-64 tw-animate-pulse tw-rounded-2xl tw-bg-ds-surface" />
        ) : error ? (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Alert tone="danger" title="Something went wrong">
              {error}
            </Alert>
            <Button variant="secondary" className="tw-self-start" onClick={reload}>
              Try again
            </Button>
          </div>
        ) : (
          <EmptyState
            icon={SearchX}
            title="Plan not found"
            description="This plan isn’t linked to your mobile number."
            action={<Button onClick={() => navigate(BACK.to)}>Back to My Plans</Button>}
          />
        )}
      </div>
    );
  }

  const { plan } = product;
  const { policy } = plan;
  const kind = planKind(policy.type);
  const status = STATUS[policy.status] ?? STATUS.active;
  const started = plan.startDate && new Date(plan.startDate) <= new Date();
  const coverNote = plan.startDate
    ? kind === 'extended' && !started
      ? `Starts after ${product.merchant ? `${product.merchant}’s` : 'the brand'} warranty ends on ${formatDay(plan.startDate)}`
      : `Cover started on ${formatDay(plan.startDate)}`
    : undefined;
  const productLine = [product.merchant, product.orderLabel, product.deliveredAt && `Delivered ${formatDay(product.deliveredAt)}`].filter(Boolean).join(' · ');
  // One approved claim per plan term for Accidental Damage (Figma T1A); counted from the customer's claims.
  const approvedClaims = claims.filter((c) => c.policyNumber === policy.name && c.outcome === 'approved').length;

  const primary = product.activeClaim ? (
    <Button leadingIcon={FileText} onClick={() => navigate(`/customer/claims/${product.activeClaim!.id}`)}>
      Track claim
    </Button>
  ) : (
    <Button leadingIcon={Plus} loading={busyPolicyId === policy.id} onClick={() => fileClaim(policy)}>
      File a claim
    </Button>
  );
  const certificate = (label: string) => (
    <Button variant="secondary" leadingIcon={Download} onClick={() => openCertificate(policy)}>
      {label}
    </Button>
  );

  const documents: ActionListItem[] = [
    { icon: Download, title: 'Plan certificate', description: policy.certificateUrl ? 'PDF' : 'Available soon', onSelect: () => openCertificate(policy) },
    {
      icon: ReceiptText,
      title: 'GST invoice',
      description: policy.premium > 0 ? `For the plan price of ${formatRupees(policy.premium)}` : 'Tax invoice for this plan',
      onSelect: () => navigate(GST_PATH),
    },
    { icon: FileText, title: 'Terms and conditions', description: 'What’s covered, exclusions and claim rules', onSelect: () => window.open(planTermsPath(policy), '_blank', 'noopener') },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <PageHeader
        title={product.title}
        badge={<StatusPill tone={status.tone}>{status.label}</StatusPill>}
        subtitle={`${plan.name} · ${policy.name}`}
        back={BACK}
        actions={
          <>
            {primary}
            {certificate('Plan certificate')}
          </>
        }
        mobileActions={
          <>
            {primary}
            {certificate('Certificate')}
          </>
        }
      />

      <div className="tw-grid tw-items-start tw-gap-5 lg:tw-grid-cols-[minmax(0,600fr)_minmax(0,456fr)] lg:tw-gap-6">
        <div className="tw-flex tw-flex-col tw-gap-5">
          <section aria-label="Product" className={`${card} tw-gap-4`}>
            <div className="tw-flex tw-items-center tw-gap-[14px]">
              <ProductThumb src={product.imageUrl} alt={product.title} />
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[3px]">
                <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{product.title}</p>
                {productLine && <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{productLine}</p>}
              </div>
            </div>
            <CoverageMeter
              plan={plan.name}
              till={`Covered till ${formatDay(plan.endDate)}`}
              left={`${formatTimeLeft(plan.endDate)} left`}
              remaining={coverRemaining(plan.startDate, plan.endDate)}
              note={coverNote}
            />
          </section>

          {product.activeClaim && (
            <Alert title={`Claim ${product.activeClaim.label} is under review`}>
              {product.activeClaim.detail ? `${product.activeClaim.detail}. ` : ''}We’ll update you within 2 working days.
            </Alert>
          )}

          <Coverage policy={policy} />
          <HowClaimsWork />
        </div>

        <div className="tw-flex tw-flex-col tw-gap-5">
          <section aria-labelledby="plan-details" className={`${card} tw-gap-2`}>
            <h2 id="plan-details" className="tw-text-[17px] tw-font-semibold tw-leading-6 tw-text-ds-text-primary">
              Plan details
            </h2>
            <KeyValueList rows={detailRows(product, plan)} />
          </section>

          {kind === 'accidental' && (
            <section aria-label="Claims used" className={`${card} tw-gap-2.5`}>
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <p className="tw-text-[14px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">Claims used</p>
                <p className="tw-text-[17px] tw-font-bold tw-leading-6 tw-text-ds-text-primary">{Math.min(approvedClaims, 1)} of 1</p>
              </div>
              <ProgressBar value={Math.min(approvedClaims, 1)} label="Claims used" />
              <p className="tw-text-[12px] tw-leading-4 tw-text-ds-text-secondary">One approved claim per plan term. The plan ends after a replacement.</p>
            </section>
          )}

          <ActionList items={documents} />

          <WhatsAppContactCard title="File or track a claim on WhatsApp" description="The quickest way to file, track or ask about a claim." />
        </div>
      </div>
    </div>
  );
};

export default PlanDetailsPage;
